import { BotIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasInput,
  CanvasPercentageInput,
  CanvasSimpleSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  ModelProbabilityShape,
  modelProbabilityType,
  ComparisonOperator,
  comparisonOperators,
  ModelProbabilityProps,
  metadataType,
  Dimensions,
} from "@/components/canvas-editor/shapes/shapes";
import { ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isModelProbabilityShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is ModelProbabilityShape {
  return shape.type === modelProbabilityType;
}

export class ModelProbabilityTool extends ToolStateNode {
  static override id = modelProbabilityType;

  static override uiToolDef = (editor: Editor) => ({
    id: modelProbabilityType,
    icon: BotIcon,
    label: ModelProbabilityShapeUtil.docs.title,
    defaultColor: ModelProbabilityShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(modelProbabilityType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={ModelProbability}
        util={ModelProbabilityShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: modelProbabilityType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class ModelProbabilityShapeUtil extends BindableUtil<ModelProbabilityShape> {
  static override migrations = createMigrations(modelProbabilityType);
  static override type = modelProbabilityType;

  override getType() {
    return modelProbabilityType;
  }

  static override docs = {
    defaultColor: "pink" as const,
    title: "Model Probability",
    description: (
      <div className="flex flex-col gap-2">
        <div>Run the post's text against a pretrained model.</div>
        <div>
          The model should return a probability between 0 and 1 for the post's
          likelyhood of being a match.
        </div>
      </div>
    ),
  };

  static override props: RecordProps<ModelProbabilityShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    modelName: T.string,
    operator: T.literalEnum(...comparisonOperators),
    threshold: T.number,
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 300,
      minW: 300,
      h: 134,
      modelName: "all-MiniLM-L6-v2",
      operator: ">=" as const,
      threshold: 0.75,
      metadata: undefined,
    };
  }

  override component(shape: ModelProbabilityShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <ModelProbability
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function ModelProbability({
  isSelected,
  isBound,
  shape,
  className,
  updateShape,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: ModelProbabilityShape;
  className?: string;
  updateShape: (
    shape: ModelProbabilityShape,
    props: Partial<ModelProbabilityProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color ||
          ModelProbabilityShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={ModelProbabilityShapeUtil.docs}
        />
        <BlockBody>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <SimpleTooltip message="The name of the transformer model used for embeddings.">
              <CanvasInput
                aria-label="Model Name"
                value={shape.props.modelName}
                placeholder="Model Name"
                onValueChange={(modelName) => updateShape(shape, { modelName })}
              />
            </SimpleTooltip>
            <div className="flex gap-2">
              <CanvasSimpleSelect
                aria-label="Comparison Operator"
                className="w-20"
                tooltip="Comparison operator"
                value={shape.props.operator}
                options={comparisonOperators}
                onValueChange={(v) =>
                  updateShape(shape, { operator: v as ComparisonOperator })
                }
              />
              <SimpleTooltip message="The similarity threshold.">
                <CanvasPercentageInput
                  aria-label="Threshold"
                  value={shape.props.threshold}
                  placeholder="Threshold"
                  className="flex-1"
                  onValueChange={(value) =>
                    updateShape(shape, { threshold: value })
                  }
                />
              </SimpleTooltip>
            </div>
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
